/**
 * pointermove / 지도 hover·pan 성능 디버그
 *
 * 콘솔 필터: panel-pointer
 * 기본: OFF (localStorage '1' 또는 window.__PANEL_POINTER_PERF__ 로 켜기)
 * 끄기: localStorage.setItem('PANEL_POINTER_PERF', '0')
 * 켜기: localStorage.setItem('PANEL_POINTER_PERF', '1') 또는 window.__PANEL_POINTER_PERF__ = true
 */

const NS = '[panel-pointer]';
const SLOW_MS = 50;

/** @type {{ t0: number, dragging: boolean, pixel: number[]|null, eventId: number } | null} */
let activePointerFrame = null;
let pointerEventSeq = 0;

/** @type {{ count: number, ms: number }} */
let styleStats = { count: 0, ms: 0 };

/** @type {Record<string, number>} */
let handlerStats = {};

/** @type {string|null} */
let lastMarkedEvent = null;
let lastMarkedEventAt = 0;
let lastSlowLogAt = 0;
const SLOW_LOG_INTERVAL_MS = 500;

export function isPanelPointerMovePerfEnabled() {
  if (typeof window !== 'undefined') {
    if (window.__PANEL_POINTER_PERF__ === true) {
      return true;
    }
    if (window.localStorage?.getItem('PANEL_POINTER_PERF') === '0') {
      return false;
    }
    if (window.localStorage?.getItem('PANEL_POINTER_PERF') === '1') {
      return true;
    }
  }
  return false;
}

/** SLOW payload → 짧은 원인 힌트 (콘솔에서 바로 읽기) */
function buildPointerMoveDiagnosisHint(payload) {
  const hints = [];
  const { styleMs = 0, styleCalls = 0, handlerTotalMs = 0, handlers = {}, reason, unattributedMs = 0 } =
    payload;

  if (styleMs >= SLOW_MS) {
    hints.push(
      styleCalls > 0
        ? `styleVectorFeature ~${styleCalls}회 (${styleMs}ms) — feature 많을수록 증가`
        : `styleMs=${styleMs}ms`
    );
  }
  if (handlerTotalMs >= SLOW_MS) {
    const top = Object.entries(handlers).sort((a, b) => b[1] - a[1])[0];
    if (top) {
      hints.push(`handler '${top[0]}' ${top[1]}ms`);
    }
  }
  if (handlers['ctrl-drag-copy'] >= 20) {
    hints.push('Ctrl+드래그 복사 중 (clone geometry + placement preview)');
  }
  if (handlers['paste-preview'] >= 20) {
    hints.push('붙여넣기 preview pointermove');
  }
  if (unattributedMs >= SLOW_MS) {
    hints.push(
      reason === 'rendercomplete'
        ? `OL Canvas repaint ~${unattributedMs}ms (style/handler 외)`
        : `OL·브라우저 파이프라인 ~${unattributedMs}ms (${reason})`
    );
  }
  if (!hints.length) {
    hints.push('50ms+ 프레임 — payload.handlers·styleCalls·featureCount 확인');
  }
  return hints.join(' | ');
}

export function panelPointerMovePerfMarkEvent(label, detail = {}) {
  if (!isPanelPointerMovePerfEnabled()) {
    return;
  }
  lastMarkedEvent = label;
  lastMarkedEventAt = performance.now();
  console.log(NS, 'event', label, detail, `@${lastMarkedEventAt.toFixed(1)}ms`);
}

export function panelPointerMovePerfBeginHandler(label) {
  if (!isPanelPointerMovePerfEnabled() || !activePointerFrame) {
    return null;
  }
  return { label, t0: performance.now() };
}

export function panelPointerMovePerfEndHandler(token, extra) {
  if (!token || !isPanelPointerMovePerfEnabled() || !activePointerFrame) {
    return;
  }
  const ms = performance.now() - token.t0;
  handlerStats[token.label] = (handlerStats[token.label] || 0) + ms;
  if (ms >= SLOW_MS) {
    console.warn(NS, `handler slow: ${token.label}`, {
      ms: ms.toFixed(1),
      ...extra
    });
  }
}

export function panelPointerMovePerfRecordStyle(ms) {
  if (!activePointerFrame || !isPanelPointerMovePerfEnabled()) {
    return;
  }
  styleStats.count += 1;
  styleStats.ms += ms;
}

/**
 * @param {object} options
 * @param {import('ol/Map').default} options.map
 * @param {HTMLElement} [options.viewport]
 * @param {() => object} [options.getContext]
 * @returns {() => void} cleanup
 */
export function installPanelPointerMovePerf({ map, viewport, getContext } = {}) {
  if (!isPanelPointerMovePerfEnabled() || !map?.on) {
    return () => {};
  }

  const cleanups = [];

  const onPointerMove = evt => {
    pointerEventSeq += 1;
    activePointerFrame = {
      t0: performance.now(),
      dragging: Boolean(evt.dragging),
      pixel: evt.pixel ? [evt.pixel[0], evt.pixel[1]] : null,
      eventId: pointerEventSeq
    };
    styleStats = { count: 0, ms: 0 };
    handlerStats = {};
  };
  map.on('pointermove', onPointerMove);
  cleanups.push(() => map.un('pointermove', onPointerMove));

  const flushSlowFrame = (reason, extra = {}) => {
    if (!activePointerFrame) {
      return;
    }
    const totalMs = performance.now() - activePointerFrame.t0;
    const frame = activePointerFrame;
    activePointerFrame = null;

    const handlerTotalMs = Object.values(handlerStats).reduce((sum, ms) => sum + ms, 0);

    if (totalMs < SLOW_MS && styleStats.ms < SLOW_MS && handlerTotalMs < SLOW_MS) {
      return;
    }

    const ctx = typeof getContext === 'function' ? getContext() : {};
    const sinceEventMs =
      lastMarkedEventAt > 0 ? Number((performance.now() - lastMarkedEventAt).toFixed(1)) : null;

    const styleMs = Number(styleStats.ms.toFixed(1));
    const handlerTotalMsRounded = Number(handlerTotalMs.toFixed(1));
    const payload = {
      totalMs: Number(totalMs.toFixed(1)),
      reason,
      dragging: frame.dragging,
      eventId: frame.eventId,
      styleCalls: styleStats.count,
      styleMs,
      avgStyleMs:
        styleStats.count > 0 ? Number((styleStats.ms / styleStats.count).toFixed(3)) : 0,
      handlerTotalMs: handlerTotalMsRounded,
      unattributedMs: Number(
        Math.max(0, totalMs - styleStats.ms - handlerTotalMs).toFixed(1)
      ),
      handlers: Object.fromEntries(
        Object.entries(handlerStats).map(([key, ms]) => [key, Number(ms.toFixed(2))])
      ),
      lastEvent: lastMarkedEvent,
      msSinceLastEvent: sinceEventMs,
      ...ctx,
      ...extra
    };
    payload.diagnosisHint = buildPointerMoveDiagnosisHint(payload);

    const now = performance.now();
    if (now - lastSlowLogAt < SLOW_LOG_INTERVAL_MS) {
      return;
    }
    lastSlowLogAt = now;
    console.warn(NS, `SLOW pointermove ${payload.totalMs}ms`, payload);
    console.info(NS, 'hint →', payload.diagnosisHint);
  };

  const onRenderComplete = () => {
    if (!activePointerFrame) {
      return;
    }
    flushSlowFrame('rendercomplete');
  };
  map.on('rendercomplete', onRenderComplete);
  cleanups.push(() => map.un('rendercomplete', onRenderComplete));

  console.log(NS, 'installed', {
    slowThresholdMs: SLOW_MS,
    filter: NS,
    off: "localStorage.setItem('PANEL_POINTER_PERF','0'); location.reload()",
    fields: 'styleCalls/styleMs=styleVectorFeature, handlers=우리 코드, unattributedMs=OL Canvas 등'
  });

  return () => {
    cleanups.forEach(fn => fn());
    activePointerFrame = null;
  };
}
